import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  ArrowDownToLine, 
  CheckCircle, 
  XCircle, 
  Clock, 
  DollarSign, 
  Settings,
  AlertTriangle,
  TrendingDown
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminAutoWithdrawalsPage = () => {
  const [loading, setLoading] = useState(true);
  const [pendingWithdrawals, setPendingWithdrawals] = useState([]);
  const [processedWithdrawals, setProcessedWithdrawals] = useState([]);
  const [stats, setStats] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
    total_amount: 0
  });
  const [selectedTab, setSelectedTab] = useState('pending');
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    fetchWithdrawals();
  }, []);

  const fetchWithdrawals = async () => {
    try {
      // Fetch pending withdrawals
      const pendingRes = await fetch(`${API_URL}/api/admin/withdrawals?status=pending`, {
        credentials: 'include'
      });
      if (pendingRes.ok) {
        const data = await pendingRes.json();
        setPendingWithdrawals(data.withdrawals || []);
      }

      // Fetch processed withdrawals
      const processedRes = await fetch(`${API_URL}/api/admin/withdrawals?status=completed,rejected&limit=50`, {
        credentials: 'include'
      });
      if (processedRes.ok) {
        const data = await processedRes.json();
        setProcessedWithdrawals(data.withdrawals || []);
      }

      // Calculate stats
      const pending = await fetch(`${API_URL}/api/admin/withdrawals/stats`, {
        credentials: 'include'
      });
      if (pending.ok) {
        const statsData = await pending.json();
        setStats(statsData);
      }
    } catch (error) {
      console.error('Failed to fetch withdrawals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (withdrawalId) => {
    if (!window.confirm('Approve this withdrawal request?')) return;
    
    setProcessing(withdrawalId);
    try {
      const response = await fetch(`${API_URL}/api/admin/withdrawals/${withdrawalId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({})
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Approval failed');
      }
      
      toast.success('Withdrawal approved successfully!');
      fetchWithdrawals();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (withdrawalId) => {
    const reason = window.prompt('Enter rejection reason:');
    if (!reason) return;
    
    setProcessing(withdrawalId);
    try {
      const response = await fetch(`${API_URL}/api/admin/withdrawals/${withdrawalId}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Rejection failed');
      }
      
      toast.success('Withdrawal rejected');
      fetchWithdrawals();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setProcessing(null);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400', icon: Clock },
      pending_admin_review: { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400', icon: Clock },
      approved: { color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400', icon: CheckCircle },
      completed: { color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400', icon: CheckCircle },
      rejected: { color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400', icon: XCircle },
      processing: { color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400', icon: Settings }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {status.replace('_', ' ')}
      </Badge>
    );
  };

  const WithdrawalCard = ({ withdrawal }) => {
    const isPending = withdrawal.status === 'pending' || withdrawal.status === 'pending_admin_review';
    
    return (
      <Card className="mb-4">
        <CardContent className="pt-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                  <ArrowDownToLine className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold">
                    {withdrawal.user_email || withdrawal.agent_email || 'Unknown User'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(withdrawal.created_at).toLocaleString()}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3">
                <div>
                  <p className="text-xs text-muted-foreground">Amount</p>
                  <p className="font-semibold text-lg">
                    {withdrawal.currency} {withdrawal.amount?.toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Type</p>
                  <p className="font-medium capitalize">{withdrawal.type || 'Standard'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Status</p>
                  {getStatusBadge(withdrawal.status)}
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">ID</p>
                  <p className="text-xs font-mono">{withdrawal.withdrawal_id?.slice(0, 8)}</p>
                </div>
              </div>

              {withdrawal.note && (
                <div className="p-3 bg-muted rounded-lg mb-3">
                  <p className="text-sm"><strong>Note:</strong> {withdrawal.note}</p>
                </div>
              )}

              {withdrawal.rejection_reason && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800 mb-3">
                  <p className="text-sm text-red-900 dark:text-red-100">
                    <strong>Rejection Reason:</strong> {withdrawal.rejection_reason}
                  </p>
                </div>
              )}

              {withdrawal.crypto_address && (
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <p className="text-xs text-muted-foreground mb-1">Crypto Address ({withdrawal.network})</p>
                  <p className="text-xs font-mono break-all">{withdrawal.crypto_address}</p>
                </div>
              )}
            </div>

            {isPending && (
              <div className="flex flex-col gap-2 ml-4">
                <Button
                  size="sm"
                  onClick={() => handleApprove(withdrawal.withdrawal_id)}
                  disabled={processing === withdrawal.withdrawal_id}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Approve
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => handleReject(withdrawal.withdrawal_id)}
                  disabled={processing === withdrawal.withdrawal_id}
                >
                  <XCircle className="h-4 w-4 mr-1" />
                  Reject
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <AdminLayout title="Auto-Withdrawals">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-lg">Loading withdrawals...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Auto-Withdrawals Management">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-4xl font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Auto-Withdrawals
          </h1>
          <p className="text-muted-foreground">
            Review and approve withdrawal requests from users and agents
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Pending</p>
                  <h3 className="text-2xl font-bold">{stats.pending || 0}</h3>
                </div>
                <Clock className="h-8 w-8 text-yellow-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Approved</p>
                  <h3 className="text-2xl font-bold">{stats.approved || 0}</h3>
                </div>
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Rejected</p>
                  <h3 className="text-2xl font-bold">{stats.rejected || 0}</h3>
                </div>
                <XCircle className="h-8 w-8 text-red-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Amount</p>
                  <h3 className="text-2xl font-bold">R {stats.total_amount?.toFixed(2) || '0.00'}</h3>
                </div>
                <DollarSign className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-4">
              <Button
                variant={selectedTab === 'pending' ? 'default' : 'outline'}
                onClick={() => setSelectedTab('pending')}
              >
                Pending ({pendingWithdrawals.length})
              </Button>
              <Button
                variant={selectedTab === 'processed' ? 'default' : 'outline'}
                onClick={() => setSelectedTab('processed')}
              >
                Processed ({processedWithdrawals.length})
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {selectedTab === 'pending' && (
              <div>
                {pendingWithdrawals.length === 0 ? (
                  <div className="text-center py-12">
                    <TrendingDown className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No pending withdrawals</p>
                  </div>
                ) : (
                  <div>
                    {pendingWithdrawals.map(withdrawal => (
                      <WithdrawalCard key={withdrawal.withdrawal_id} withdrawal={withdrawal} />
                    ))}
                  </div>
                )}
              </div>
            )}

            {selectedTab === 'processed' && (
              <div>
                {processedWithdrawals.length === 0 ? (
                  <div className="text-center py-12">
                    <AlertTriangle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No processed withdrawals</p>
                  </div>
                ) : (
                  <div>
                    {processedWithdrawals.map(withdrawal => (
                      <WithdrawalCard key={withdrawal.withdrawal_id} withdrawal={withdrawal} />
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};
